import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { ServiceVariant } from '@/lib/serviceCode'

const STORAGE_KEY = 'bzuco-admin-clipboard'

export interface ClipboardLogin {
    username: string
    password: string
}

export interface ClipboardCodeItem {
    id: number
    name: string
    eventName?: string
}

export interface ClipboardCode {
    id: number
    kind: 'ticketsets' | 'zones' | 'login'
    login: ClipboardLogin | null
    items: ClipboardCodeItem[]
    // Optional custom print label for ticketsets/zones cards. When set, the
    // print sheet shows this text instead of the item-name list. The list
    // remains visible in the clipboard UI alongside the custom text.
    printLabel?: string
}

export interface PageBreak {
    id: number
    kind: 'page-break'
    title: string
}

export interface ClipboardService {
    id: number
    kind: 'service'
    service: ServiceVariant
}

export interface ClipboardTicket {
    id: number
    kind: 'ticket'
    code: string
    // Optional custom print label (same behaviour as ticketsets/zones): when set,
    // the print sheet shows this text instead of the ticket code.
    printLabel?: string
}

export type ClipboardItem = ClipboardCode | PageBreak | ClipboardService | ClipboardTicket

interface AddTicketsetsPayload {
    kind: 'ticketsets' | 'zones'
    item: ClipboardCodeItem
}

interface AddLoginPayload {
    kind: 'login'
    login: ClipboardLogin
    item: ClipboardCodeItem
}

export type AddPayload = AddTicketsetsPayload | AddLoginPayload

export interface MergeResult {
    ok: boolean
    reason?: 'kind-mismatch' | 'not-mergeable'
}

export interface ClipboardPage {
    pageBreakId: number | null
    title: string
    codes: (ClipboardCode | ClipboardService | ClipboardTicket)[]
}

function load(): ClipboardItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const parsed = JSON.parse(raw)
        if (!Array.isArray(parsed)) return []
        // Both old (codes without page-break) and new (items with page-break) formats
        // share the same array shape; the new code consumes them identically.
        return parsed as ClipboardItem[]
    } catch {
        return []
    }
}

function persist(items: ClipboardItem[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
}

function isCode(item: ClipboardItem): item is ClipboardCode | ClipboardService | ClipboardTicket {
    return item.kind !== 'page-break'
}

// The store stores titles as-is (possibly empty). The view formats the
// placeholder using i18n so the displayed text follows the active locale.
function finalizePage(page: ClipboardPage, _pageNumber: number): ClipboardPage {
    return page
}

// Whether generated QR codes wrap deep-linkable payloads (login / ticketsets /
// zones) into a deep-link URL. Default true. When false, those codes are emitted
// as the bare payload (like the reader/service/ticket codes). Persisted in its
// own localStorage key so the preference survives reloads.
const URL_IN_QR_KEY = 'bzuco-admin-clipboard-url-in-qr'

function loadUrlInQr(): boolean {
    // Default true unless explicitly turned off.
    return localStorage.getItem(URL_IN_QR_KEY) !== 'false'
}

export const useClipboardStore = defineStore('clipboard', () => {
    const items = ref<ClipboardItem[]>(load())
    let nextId = items.value.reduce((m, it) => Math.max(m, it.id), 0) + 1

    const urlInQr = ref<boolean>(loadUrlInQr())

    watch(items, value => persist(value), { deep: true })
    watch(urlInQr, value => localStorage.setItem(URL_IN_QR_KEY, String(value)))

    const count = computed(() => items.value.filter(isCode).length)

    const pages = computed<ClipboardPage[]>(() => {
        const result: ClipboardPage[] = []
        let current: ClipboardPage = { pageBreakId: null, title: '', codes: [] }
        let pageNumber = 1

        for (const it of items.value) {
            if (it.kind === 'page-break') {
                // Flush the in-progress page (implicit or explicit) before opening a new one.
                if (current.codes.length > 0 || current.pageBreakId !== null) {
                    result.push(finalizePage(current, pageNumber++))
                }
                current = { pageBreakId: it.id, title: it.title, codes: [] }
            } else {
                current.codes.push(it)
            }
        }
        // Always push the trailing page so a page-break with no codes after it still renders.
        if (current.codes.length > 0 || current.pageBreakId !== null) {
            result.push(finalizePage(current, pageNumber))
        }
        return result
    })

    function add(payload: AddPayload) {
        // Each add creates a standalone code, appended to the end of items
        // (= end of the last page). Grouping is now done via drag-and-drop.
        if (payload.kind === 'login') {
            items.value.push({
                id: nextId++,
                kind: 'login',
                login: payload.login,
                items: [payload.item]
            })
            return
        }
        items.value.push({
            id: nextId++,
            kind: payload.kind,
            login: null,
            items: [payload.item]
        })
    }

    function addService(service: ServiceVariant): number {
        const id = nextId++
        items.value.push({ id, kind: 'service', service })
        return id
    }

    function addTicket(code: string): number {
        // Dedup: a ticket with the same code is only added once.
        const existing = items.value.find(it => it.kind === 'ticket' && it.code === code)
        if (existing) return existing.id
        const id = nextId++
        items.value.push({ id, kind: 'ticket', code })
        return id
    }

    function remove(id: number) {
        items.value = items.value.filter(it => it.id !== id)
    }

    function move(fromIndex: number, toIndex: number) {
        if (fromIndex < 0 || fromIndex >= items.value.length) return
        if (toIndex < 0 || toIndex >= items.value.length) return
        if (fromIndex === toIndex) return
        const next = [...items.value]
        const [moved] = next.splice(fromIndex, 1)
        next.splice(toIndex, 0, moved)
        items.value = next
    }

    function insertPageBreak(afterIndex?: number, title = ''): number {
        const pb: PageBreak = { id: nextId++, kind: 'page-break', title }
        if (afterIndex === undefined || afterIndex >= items.value.length - 1) {
            items.value.push(pb)
        } else {
            const next = [...items.value]
            next.splice(afterIndex + 1, 0, pb)
            items.value = next
        }
        return pb.id
    }

    function setPageTitle(id: number, title: string) {
        items.value = items.value.map(it => {
            if (it.id !== id || it.kind !== 'page-break') return it
            return { ...it, title }
        })
    }

    // Custom print label for a ticketsets/zones card. An empty/whitespace label
    // clears it (falls back to the item-name list at print time). No-op for
    // login, page-break and service items.
    function setPrintLabel(id: number, label: string) {
        const trimmed = label.trim()
        items.value = items.value.map(it => {
            if (it.id !== id) return it
            if (it.kind !== 'ticketsets' && it.kind !== 'zones' && it.kind !== 'ticket') return it
            return { ...it, printLabel: trimmed || undefined }
        })
    }

    function merge(sourceId: number, targetId: number): MergeResult {
        if (sourceId === targetId) return { ok: false, reason: 'not-mergeable' }
        const source = items.value.find(it => it.id === sourceId)
        const target = items.value.find(it => it.id === targetId)
        if (!source || !target) return { ok: false, reason: 'not-mergeable' }
        if (
            source.kind === 'page-break' || target.kind === 'page-break' ||
            source.kind === 'service' || target.kind === 'service' ||
            source.kind === 'ticket' || target.kind === 'ticket'
        ) {
            return { ok: false, reason: 'not-mergeable' }
        }

        // LOGIN → TICKETS_T or TICKETS_Z: attach the login as prefix, SOURCE STAYS.
        if (source.kind === 'login' && (target.kind === 'ticketsets' || target.kind === 'zones')) {
            items.value = items.value.map(it => {
                if (it.id !== target.id) return it
                return { ...it, login: source.login }
            })
            return { ok: true }
        }

        // Same payload kind (ticketsets→ticketsets or zones→zones): merge items,
        // dedup by id. Source is removed; target keeps its login if it has one,
        // otherwise inherits the source's login so dragging a code-with-login
        // into a plain code doesn't silently throw the login away.
        if (
            (source.kind === 'ticketsets' && target.kind === 'ticketsets') ||
            (source.kind === 'zones' && target.kind === 'zones')
        ) {
            const existingIds = new Set(target.items.map(i => i.id))
            const additions = source.items.filter(i => !existingIds.has(i.id))
            const mergedLogin = target.login ?? source.login
            items.value = items.value
                .map(it =>
                    it.id === target.id
                        ? {
                              ...(it as ClipboardCode),
                              items: [...(it as ClipboardCode).items, ...additions],
                              login: mergedLogin
                          }
                        : it
                )
                .filter(it => it.id !== source.id)
            return { ok: true }
        }

        // Everything else (T↔Z, LOGIN→LOGIN, anything→LOGIN) is disallowed.
        return { ok: false, reason: 'kind-mismatch' }
    }

    function clear() {
        items.value = []
    }

    // Reverse of merge: explode a code with multiple items or a login attached
    // back into individual codes. Login (if present) becomes its own LOGIN
    // entry and is inserted right before the split items. Each item becomes
    // a separate code of the same kind.
    //
    // Cases:
    //   • ticketsets/zones with ≥2 items, no login → N codes, one per item.
    //   • ticketsets/zones with 1 item + login    → 1 LOGIN code + 1 item code.
    //   • ticketsets/zones with ≥2 items + login  → 1 LOGIN code + N item codes.
    //   • login code or single-item without login → no-op (nothing to split).
    function split(id: number): void {
        const idx = items.value.findIndex(it => it.id === id)
        if (idx === -1) return
        const code = items.value[idx]
        if (code.kind === 'page-break' || code.kind === 'login' || code.kind === 'service' || code.kind === 'ticket') return
        const hasLogin = code.login !== null
        if (code.items.length < 2 && !hasLogin) return

        const replacements: ClipboardItem[] = []
        if (hasLogin && code.login) {
            // Re-materialize the login as its own code. We can't recover the
            // original device entry ('items' on a LOGIN code is metadata for
            // display), so reuse the username for the label.
            replacements.push({
                id: nextId++,
                kind: 'login',
                login: code.login,
                items: [{ id: 0, name: code.login.username }]
            })
        }
        for (const item of code.items) {
            replacements.push({
                id: nextId++,
                kind: code.kind,
                login: null,
                items: [item]
            })
        }
        const next = [...items.value]
        next.splice(idx, 1, ...replacements)
        items.value = next
    }

    return { items, urlInQr, count, pages, add, addService, addTicket, remove, move, insertPageBreak, setPageTitle, setPrintLabel, merge, split, clear }
})
